// @ts-nocheck
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextAreaPresentationProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { BlockProps } from "@store/Library/Components/Toolkit/Block";
import { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import { configurationFormProps } from "@store/Themes/common/props/configuratorForm";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { ChangeEvent, useEffect } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { CustomerInformationModel } from "@store/Framework/Types/ApiModels";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import validatePhone from "@insite/client-framework/Common/Utilities/validatePhone";

export type CustomerFormState = {
    firstName: string;
    lastName: string;
    companyName: string;
    phoneNumber: string;
    email: string;
};

export const toCustomerInformationModel = (form: CustomerFormState): CustomerInformationModel => {
    return {
        firstName: form.firstName,
        lastName: form.lastName,
        companyName: form.companyName,
        phoneNumber: form.phoneNumber,
        email: form.email,
    };
};

export const emptyCustomerFormState = (): CustomerFormState => {
    return {
        firstName: "",
        lastName: "",
        companyName: "",
        phoneNumber: "",
        email: "",
    };
};

export type CustomerFormErrors = Partial<Record<keyof CustomerFormState, string>>;

interface OwnProps {
    form: CustomerFormState;
    errors?: CustomerFormErrors;
    reloadCustomerInformationFields: boolean;
    updateCustomerState: (customerState: CustomerFormState) => void;
    onValidationChange?: (errors: Partial<Record<keyof CustomerFormState, string>>) => void;
    onHandleChange: (
        fieldName: keyof CustomerFormState,
    ) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
}

const mapStateToProps = (state: ApplicationState) => {
    const { firstName, lastName, email, isAuthenticated, isGuest, properties } = getSession(state);

    if (!isAuthenticated || isGuest) {
        return {
            initialCustomerStateLoaded: true,
            initialCustomerState: {
                firstName: firstName || "",
                lastName: lastName || "",
                companyName: "",
                phoneNumber: "",
                email: email || "",
            },
        };
    }

    const { value: shipTo } = getCurrentShipToState(state);
    const { value: billTo } = getCurrentBillToState(state);

    const userPhone = properties ? properties["userPhone"] : undefined;

    return {
        initialCustomerStateLoaded: Boolean(shipTo?.id && billTo?.id),
        initialCustomerState: {
            firstName: firstName || shipTo?.firstName || billTo?.firstName || "",
            lastName: lastName || shipTo?.lastName || billTo?.lastName || "",
            companyName: shipTo?.companyName || billTo?.companyName || "",
            phoneNumber: userPhone || shipTo?.phone || billTo?.phone || "",
            email: email || shipTo?.email || billTo?.email || "",
        },
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

interface CustomerInformationFieldsStyles {
    block?: BlockProps;
    container?: GridContainerProps;
    formContainer?: GridContainerProps;
    fieldItem?: GridItemProps;
    smallerFieldItem?: GridItemProps;
    fullWidthItem?: GridItemProps;
    actionItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    sectionText?: TypographyPresentationProps;
    introText?: TypographyPresentationProps;
    questionnaireText?: TypographyPresentationProps;
    identifierField?: TextFieldPresentationProps;
    selectField?: SelectPresentationProps;
    commentField?: TextAreaPresentationProps;
    clearButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
    successInfosBox?: InfosBoxProps;
    successModal?: ModalPresentationProps;
    continueButton?: ButtonPresentationProps;
    errorInfosBox?: InfosBoxProps;
    loadingOverlay?: LoadingOverlayProps;
}

export const customerInformationFieldsStyles: CustomerInformationFieldsStyles = {
    block: configurationFormProps.block,
    container: {
        gap: 32,
        css: css`
            padding: ${pxToRem(16)};
        `,
    },
    formContainer: configurationFormProps.formContainer,
    fieldItem: configurationFormProps.fieldItem,
    smallerFieldItem: configurationFormProps.smallerFieldItem,
    fullWidthItem: configurationFormProps.fullWidthItem,
    actionItem: configurationFormProps.actionItem,
    titleText: configurationFormProps.titleText,
    sectionText: configurationFormProps.sectionText,
    introText: configurationFormProps.introText,
    questionnaireText: configurationFormProps.questionnaireText,
    clearButton: configurationFormProps.clearButton,
    submitButton: configurationFormProps.submitButton,
    successInfosBox: configurationFormProps.successInfosBox,
    successModal: configurationFormProps.successModal,
    continueButton: configurationFormProps.continueButton,
    errorInfosBox: configurationFormProps.errorInfosBox,
    loadingOverlay: configurationFormProps.loadingOverlay,
};

const styles = customerInformationFieldsStyles;

const CustomerInformationFields = ({
    form,
    errors,
    reloadCustomerInformationFields,
    updateCustomerState,
    onValidationChange,
    onHandleChange,
    initialCustomerState,
    initialCustomerStateLoaded,
}: Props) => {
    const validate = (): boolean => {
        const newErrors: Partial<Record<keyof CustomerFormState, string>> = {};

        if (!form.firstName) {
            newErrors.firstName = translate("Fls.CustomerInformation.FirstName.Required");
        }

        if (!form.lastName) {
            newErrors.lastName = translate("Fls.CustomerInformation.LastName.Required");
        }

        if (!form.companyName) {
            newErrors.companyName = translate("Fls.CustomerInformation.CompanyName.Required");
        }

        if (!form.phoneNumber) {
            newErrors.phoneNumber = translate("Fls.CustomerInformation.PhoneNumber.Required");
        } else if (!validatePhone(form.phoneNumber)) {
            newErrors.phoneNumber = translate("Fls.CustomerInformation.PhoneNumber.Invalid");
        }

        if (!form.email) {
            newErrors.email = translate("Fls.CustomerInformation.Email.Required");
        } else if (!validateEmail(form.email)) {
            newErrors.email = translate("Fls.CustomerInformation.Email.Invalid");
        }

        if (onValidationChange) {
            onValidationChange(newErrors);
        }

        return Object.keys(newErrors).length === 0;
    };

    useEffect(() => {
        if (onValidationChange) {
            validate();
        }
    }, [form.firstName, form.lastName, form.companyName, form.phoneNumber, form.email]);

    useEffect(() => {
        if (!initialCustomerStateLoaded) {
            return;
        }

        if (!reloadCustomerInformationFields) {
            return;
        }

        updateCustomerState({
            ...form,
            firstName: initialCustomerState?.firstName,
            lastName: initialCustomerState?.lastName,
            companyName: initialCustomerState?.companyName,
            phoneNumber: initialCustomerState?.phoneNumber,
            email: initialCustomerState?.email,
        });
    }, [initialCustomerState, reloadCustomerInformationFields]);

    return (
        <LoadingOverlay loading={!initialCustomerStateLoaded} {...styles.loadingOverlay}>
            <GridContainer {...styles.container}>
                <GridItem {...styles.fieldItem}>
                    <TextField
                        {...styles.identifierField}
                        label={`${translate("Fls.CustomerInformation.FirstName.Label")}*`}
                        placeholder={translate("Fls.CustomerInformation.FirstName.Placeholder")}
                        value={form.firstName}
                        onChange={onHandleChange("firstName")}
                        error={errors?.firstName}
                    />
                </GridItem>
                <GridItem {...styles.fieldItem}>
                    <TextField
                        {...styles.identifierField}
                        label={`${translate("Fls.CustomerInformation.LastName.Label")}*`}
                        placeholder={translate("Fls.CustomerInformation.LastName.Placeholder")}
                        value={form.lastName}
                        onChange={onHandleChange("lastName")}
                        error={errors?.lastName}
                    />
                </GridItem>
                <GridItem {...styles.fieldItem}>
                    <TextField
                        {...styles.identifierField}
                        label={`${translate("Fls.CustomerInformation.CompanyName.Label")}*`}
                        placeholder={translate("Fls.CustomerInformation.CompanyName.Placeholder")}
                        value={form.companyName}
                        onChange={onHandleChange("companyName")}
                        error={errors?.companyName}
                    />
                </GridItem>
                <GridItem {...styles.fieldItem}>
                    <TextField
                        {...styles.identifierField}
                        label={`${translate("Fls.CustomerInformation.PhoneNumber.Label")}*`}
                        placeholder={translate("Fls.CustomerInformation.PhoneNumber.Placeholder")}
                        value={form.phoneNumber}
                        onChange={onHandleChange("phoneNumber")}
                        error={errors?.phoneNumber}
                    />
                </GridItem>
                <GridItem {...styles.fieldItem}>
                    <TextField
                        {...styles.identifierField}
                        label={`${translate("Fls.CustomerInformation.Email.Label")}*`}
                        placeholder={translate("Fls.CustomerInformation.Email.Placeholder")}
                        value={form.email}
                        onChange={onHandleChange("email")}
                        error={errors?.email}
                    />
                </GridItem>
            </GridContainer>
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps)(CustomerInformationFields);
